import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput, {
  formatLatexPreview,
} from "../../../../components/inputs/FractionInput";

function randInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function gcd(a, b) {
  a = Math.abs(a);
  b = Math.abs(b);
  while (b) {
    const t = a % b;
    a = b;
    b = t;
  }
  return a || 1;
}

function simplifyFrac(num, den) {
  if (den === 0) return [num, den];
  let n = num;
  let d = den;
  if (d < 0) {
    n = -n;
    d = -d;
  }
  const g = gcd(n, d);
  return [n / g, d / g];
}

function fracToLatex(num, den) {
  const [n, d] = simplifyFrac(num, den);
  if (d === 1) return `${n}`;
  return `\\dfrac{${n}}{${d}}`;
}

function fracToMixedLatex(num, den) {
  const [n, d] = simplifyFrac(num, den);
  if (d === 1) return `${n}`;
  const sign = n < 0 ? "-" : "";
  const absN = Math.abs(n);
  const whole = Math.floor(absN / d);
  const rem = absN % d;
  if (whole === 0) return `${sign}\\dfrac{${absN}}{${d}}`;
  if (rem === 0) return `${sign}${whole}`;
  return `${sign}${whole}\\dfrac{${rem}}{${d}}`;
}

function fmtSigned(n) {
  return n >= 0 ? `+ ${n}` : `- ${Math.abs(n)}`;
}

function buildEquation1(k, m) {
  // x - k y = m (z czytelnym znakiem, bez "-(-y)")
  if (k === 0) return `x = ${m}`;
  if (k > 0) {
    const ky = k === 1 ? "y" : `${k}y`;
    return `x - ${ky} = ${m}`;
  }
  const ay = Math.abs(k) === 1 ? "y" : `${Math.abs(k)}y`;
  return `x + ${ay} = ${m}`;
}

function buildEquation2(p, q, r) {
  const px = p === 1 ? "x" : p === -1 ? "-x" : `${p}x`;
  const qy = q === 1 ? "y" : q === -1 ? "-y" : `${q}y`;
  let left = "";

  if (p !== 0) left += px;
  if (q !== 0) {
    if (left.length === 0) {
      left += qy;
    } else {
      left += q > 0 ? ` + ${qy}` : ` - ${Math.abs(q) === 1 ? "y" : `${Math.abs(q)}y`}`;
    }
  }
  if (left.length === 0) left = "0";
  return `${left} = ${r}`;
}

function parseVal(v) {
  if (v && typeof v === "object" && Number.isFinite(v.parsed)) return v.parsed;
  return NaN;
}

export function generateTask() {
  let k = 0;
  let m = 0;
  let p = 0;
  let q = 0;
  let d = 0;
  let t = 0;
  let r = 0;
  let yNum = 0;
  let yDen = 1;
  let xNum = 0;
  let xDen = 1;

  while (true) {
    k = randInt(-5, 5);
    if (k === 0) continue;
    m = randInt(-8, 8);
    p = randInt(-6, 6);
    q = randInt(-6, 6);
    if (p === 0 || q === 0) continue;

    d = p * k + q;
    if (d === 0) continue;

    const ad = Math.abs(d);
    const divisors = [1, 2, 3, 4, 5, 6].filter((x) => ad % x === 0);
    yDen = divisors[randInt(0, divisors.length - 1)];
    t = randInt(-8, 8);
    if (t === 0) t = 1;
    yNum = t;

    // r = d * (t / yDen) + p*m (zachowujemy całkowite r)
    r = (d * t) / yDen + p * m;
    if (!Number.isInteger(r)) continue;

    xNum = k * yNum + m * yDen;
    xDen = yDen;
    [xNum, xDen] = simplifyFrac(xNum, xDen);
    [yNum, yDen] = simplifyFrac(yNum, yDen);

    // Nie chcemy zbyt "dzikich" wyników
    if (Math.abs(xNum) > 40 || Math.abs(yNum) > 40) continue;
    break;
  }

  const eq1 = buildEquation1(k, m);
  const eq2 = buildEquation2(p, q, r);
  const systemLatex = `\\begin{cases} ${eq1} \\\\ ${eq2} \\end{cases}`;

  return {
    type: "LinearSystemSubstitutionEasyTask",
    data: {
      k,
      m,
      p,
      q,
      r,
      xNum,
      xDen,
      yNum,
      yDen,
      systemLatex,
    },
  };
}

export function renderTask({ data }) {
  const prompt = data?.neutralPrompt
    ? "Rozwi\u0105\u017c uk\u0142ad r\u00f3wna\u0144:"
    : "Rozwi\u0105\u017c uk\u0142ad r\u00f3wna\u0144 metod\u0105 podstawiania:";
  return (
    <div style={{ textAlign: "left", lineHeight: 1.6 }}>
      <p>{prompt}</p>
      <BlockMath math={data.systemLatex} />
    </div>
  );
}

export function renderInput(_task, userAnswer = {}, onChange, disabled) {
  const xVal = userAnswer?.x || "";
  const yVal = userAnswer?.y || "";

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <InlineMath math={"x ="} />
        <FractionInput
          value={xVal}
          onChange={(v) => onChange({ ...userAnswer, x: v })}
          disabled={disabled}
          label="x"
          placeholder="np. 3/2 lub 1.5"
        />
      </div>
      <div className="flex items-center gap-2">
        <InlineMath math={"y ="} />
        <FractionInput
          value={yVal}
          onChange={(v) => onChange({ ...userAnswer, y: v })}
          disabled={disabled}
          label="y"
          placeholder="np. -2/3 lub -0.67"
        />
      </div>
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  const ux = parseVal(userAnswer?.x);
  const uy = parseVal(userAnswer?.y);
  if (!Number.isFinite(ux) || !Number.isFinite(uy)) return { correct: false };

  const ex = data.xNum / data.xDen;
  const ey = data.yNum / data.yDen;
  const ok = Math.abs(ux - ex) < 0.01 && Math.abs(uy - ey) < 0.01;
  return { correct: ok };
}

export function renderAnswer({ data }) {
  return (
    <div
      className="flex flex-col"
      style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", lineHeight: 1.25 }}
    >
      <div style={{ marginBottom: "0.12rem" }}>
        <InlineMath math={`x = ${fracToMixedLatex(data.xNum, data.xDen)}`} />
      </div>
      <div>
        <InlineMath math={`y = ${fracToMixedLatex(data.yNum, data.yDen)}`} />
      </div>
    </div>
  );
}

export function renderUserAnswer(userAnswer) {
  const xRaw = userAnswer?.x?.raw || "";
  const yRaw = userAnswer?.y?.raw || "";
  if (!xRaw && !yRaw) return <i>(brak odpowiedzi)</i>;

  return (
    <div
      className="flex flex-col"
      style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", lineHeight: 1.25 }}
    >
      {xRaw ? (
        <div style={{ marginBottom: "0.12rem" }}>
          <InlineMath math={formatLatexPreview(xRaw, "x")} />
        </div>
      ) : (
        <i>(brak x)</i>
      )}
      {yRaw ? (
        <div>
          <InlineMath math={formatLatexPreview(yRaw, "y")} />
        </div>
      ) : (
        <i>(brak y)</i>
      )}
    </div>
  );
}

export function renderExplanation({ data }) {
  const { k, m, p, q, r, xNum, xDen, yNum, yDen } = data;
  const d = p * k + q;
  const pm = p * m;
  const rhs = r - pm;
  const pk = p * k;

  const yLatex = fracToMixedLatex(yNum, yDen);
  const xLatex = fracToMixedLatex(xNum, xDen);

  const ky = k === 1 ? "y" : k === -1 ? "-y" : `${k}y`;
  const eq1Original = buildEquation1(k, m);
  const eq2Original = buildEquation2(p, q, r);
  const eq1Iso = `x = ${ky} ${fmtSigned(m)}`;

  const absQy = Math.abs(q) === 1 ? "y" : `${Math.abs(q)}y`;
  const subExpr = `${ky} ${fmtSigned(m)}`;
  const substitutedX =
    p === 1
      ? `${subExpr}`
      : p === -1
      ? `-\\left(${subExpr}\\right)`
      : `${p}\\left(${subExpr}\\right)`;
  const substitutedLine = `${substitutedX} ${q >= 0 ? "+" : "-"} ${absQy} = ${r}`;

  const pkTerm = pk === 1 ? "y" : pk === -1 ? "-y" : `${pk}y`;
  const expandedLine =
    pm === 0
      ? `${pkTerm} ${q >= 0 ? "+" : "-"} ${absQy} = ${r}`
      : `${pkTerm} ${pm >= 0 ? "+" : "-"} ${Math.abs(pm)} ${q >= 0 ? "+" : "-"} ${absQy} = ${r}`;
  const beforeReductionLine =
    pm === 0
      ? null
      : `${pkTerm} ${q >= 0 ? "+" : "-"} ${absQy} = ${rhs}`;
  const reducedLeft = d === 1 ? "y" : d === -1 ? "-y" : `${d}y`;

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>
        <strong>Krok 1:</strong> Z pierwszego równania wyznaczamy{" "}
        <InlineMath math="x" />:
      </p>
      <BlockMath
        math={String.raw`\begin{cases}
${eq1Original}\ \Rightarrow\ ${eq1Iso}\\
${eq2Original}
\end{cases}`}
      />

      <p>
        <strong>Krok 2:</strong> Podstawiamy do drugiego równania:
      </p>
      <BlockMath math={substitutedLine} />
      <BlockMath math={expandedLine} />
      {beforeReductionLine ? <BlockMath math={beforeReductionLine} /> : null}
      <BlockMath math={`${reducedLeft} = ${rhs}`} />
      <BlockMath math={`y = ${yLatex}`} />

      <p>
        <strong>Krok 3:</strong> Liczymy <InlineMath math="x" /> z{" "}
        <InlineMath math={`x = ${ky} ${fmtSigned(m)}`} />:
      </p>
      <BlockMath math={`x = ${xLatex}`} />

      <p>
        <strong>Odpowiedź:</strong>{" "}
        <InlineMath math={`\\left(x,y\\right)=\\left(${xLatex}, ${yLatex}\\right)`} />
      </p>
    </div>
  );
}
